import { useMutation, useQuery } from '@tanstack/react-query';
import { ExternalLink, Play, RotateCcw } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Link } from '@tanstack/react-router';
import { JsonView } from '@/components/JsonView';
import { TraceView } from '@/components/TraceView';
import { Badge, Button, Card, ErrorNote, Field, Loading, Select, Textarea } from '@/components/ui';
import { api, apiStream } from '@/lib/api';
import { getRegion, playgroundStreamUrl } from '@/lib/regions';
import { fmtInt, fmtMs, fmtUsd } from '@/lib/format';
import { canEdit, rk, useMe } from '@/lib/hooks';
import type { AgentDetail, ChatMessage, TraceDetail } from '@/lib/types';

type RunResponse = { ok: boolean; output?: unknown; text?: string; traceId: string | null; usage?: { inputTokens: number; outputTokens: number; costUsd: number | null }; activeAgent?: string; error?: string };

/** Playground fields from a logged request's input (see the trace page's replay rules). */
const replayInput = (input: unknown) => {
  if (!input || typeof input !== 'object') return null;
  const i = input as { variables?: unknown; messages?: unknown; input?: unknown };
  const values: Record<string, string> = {};
  // Non-string values (numbers, booleans, objects) are sent back as they were, not as text.
  const typed: Record<string, unknown> = {};
  if (i.variables && typeof i.variables === 'object')
    for (const [k, v] of Object.entries(i.variables as Record<string, unknown>)) {
      values[k] = typeof v === 'string' ? v : JSON.stringify(v, null, 2);
      if (typeof v !== 'string') typed[k] = v;
    }
  const messages = Array.isArray(i.messages) ? (i.messages as ChatMessage[]).filter((m) => m && typeof m.content === 'string') : [];
  return { values, typed, messages, input: typeof i.input === 'string' ? i.input : '' };
};

export const AgentPlayground = ({ detail, replayTraceId }: { detail: AgentDetail; replayTraceId?: string }) => {
  const me = useMe();
  const draft = detail.versions.find((v) => v.status === 'draft');
  const [versionId, setVersionId] = useState(draft?.id ?? detail.agent.publishedVersionId ?? detail.versions[0]?.id ?? '');
  const version = detail.versions.find((v) => v.id === versionId);
  const vars = version?.config.variables ?? [];
  const [values, setValues] = useState<Record<string, string>>({});
  const [typed, setTyped] = useState<Record<string, unknown>>({});
  const [input, setInput] = useState('');
  const [history, setHistory] = useState<ChatMessage[]>([]);
  const [mocks, setMocks] = useState('');
  const [last, setLast] = useState<RunResponse | null>(null);
  // Live view while a streaming run is in flight.
  const [live, setLive] = useState<{ text: string; events: string[] } | null>(null);
  const streamUrl = playgroundStreamUrl(getRegion());

  const replay = useQuery({
    queryKey: rk('trace', replayTraceId),
    queryFn: () => api<TraceDetail>(`/v1/traces/${replayTraceId}`),
    enabled: Boolean(replayTraceId),
  });
  const replayed = useRef(false);
  useEffect(() => {
    if (replayed.current || !replay.data) return;
    replayed.current = true;
    const r = replayInput(replay.data.trace.input);
    if (!r) return;
    setValues(r.values);
    setTyped(r.typed);
    setHistory(r.messages);
    setInput(r.input);
  }, [replay.data]);

  /** The typed value when a replayed field is untouched, otherwise what's in the box. */
  const variablesToSend = () =>
    Object.fromEntries(Object.entries(values).map(([k, v]) => [k, k in typed && v === JSON.stringify(typed[k], null, 2) ? typed[k] : v]));

  const run = useMutation({
    mutationFn: () => {
      let mockTools: unknown = undefined;
      if (mocks.trim()) {
        try {
          mockTools = JSON.parse(mocks);
        } catch {
          mockTools = mocks.trim();
        }
      }
      const body = { agentId: detail.agent.id, versionId, variables: variablesToSend(), input: input || undefined, messages: history, mockTools };
      if (!streamUrl) return api<RunResponse>('/v1/playground/run', { body });
      // Streaming: show tokens and tool/handoff events as they arrive.
      return (async () => {
        setLive({ text: '', events: [] });
        let final: RunResponse = { ok: false, error: 'Stream ended without a result', traceId: null };
        await apiStream(streamUrl, body, (e) => {
          if (e.type === 'delta') setLive((l) => (l ? { ...l, text: l.text + e.text } : l));
          else if (e.type === 'tool_call') setLive((l) => (l ? { ...l, events: [...l.events, `→ ${e.name}(${e.arguments})`] } : l));
          else if (e.type === 'tool_result') setLive((l) => (l ? { ...l, events: [...l.events, `← ${e.name}: ${String(e.output).slice(0, 200)}`] } : l));
          else if (e.type === 'handoff') setLive((l) => (l ? { ...l, events: [...l.events, `⇢ handed off to ${e.to}`] } : l));
          else if (e.type === 'done') final = { ok: true, output: e.output, text: e.text, traceId: e.traceId, usage: e.usage, activeAgent: e.activeAgent };
          else if (e.type === 'error') final = { ok: false, error: e.message, traceId: e.traceId };
        });
        setLive(null);
        return final;
      })();
    },
    onSuccess: (res) => {
      setLast(res);
      if (res.ok && (input || !history.length)) setHistory([...history, ...(input ? [{ role: 'user' as const, content: input }] : []), { role: 'assistant', content: res.text ?? '' }]);
      setInput('');
    },
  });

  const trace = useQuery({
    queryKey: rk('trace', last?.traceId),
    queryFn: () => api<TraceDetail>(`/v1/traces/${last!.traceId}`),
    enabled: Boolean(last?.traceId),
    retry: 3,
    retryDelay: 700,
  });

  if (!canEdit(me.data)) return <p className="text-[13px] text-ink-3">The playground needs editor access (it calls real models and costs money).</p>;

  return (
    <div className="grid gap-5 xl:grid-cols-[420px_minmax(0,1fr)]">
      <div className="flex flex-col gap-5">
        <Card title="Run">
          <div className="flex flex-col gap-3 p-4">
            {replayTraceId && (
              <p className="rounded-md bg-line-2/60 px-2.5 py-2 text-[12px] text-ink-2">
                {replay.isLoading ? 'Loading the logged request…' : replay.error ? `Couldn’t load request ${replayTraceId}.` : (
                  <>
                    Inputs from{' '}
                    <Link to="/requests/$traceId" params={{ traceId: replayTraceId }} className="mono hover:underline">
                      {replayTraceId.slice(0, 18)}…
                    </Link>
                    {replay.data?.trace.version ? ` (ran on v${replay.data.trace.version})` : ''}. Pick a version and run to compare.
                  </>
                )}
              </p>
            )}
            <Field label="Version">
              <Select value={versionId} onChange={(e) => setVersionId(e.target.value)}>
                {detail.versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version} {v.status === 'draft' ? '(draft, saved)' : v.id === detail.agent.publishedVersionId ? '(live)' : ''}
                  </option>
                ))}
              </Select>
            </Field>
            {vars.map((v) => (
              <Field key={v.name} label={<span className="mono">{v.name}{v.required ? ' *' : ''}</span>} hint={v.description}>
                <Textarea rows={v.name.toLowerCase().includes('text') || v.name.toLowerCase().includes('cv') ? 5 : 2} value={values[v.name] ?? ''} onChange={(e) => setValues({ ...values, [v.name]: e.target.value })} />
              </Field>
            ))}
            {history.length > 0 && (
              <div className="flex max-h-72 flex-col gap-2 overflow-auto rounded-md border border-line bg-line-2/40 p-2">
                {history.map((m, i) => (
                  <div key={i} className={m.role === 'user' ? 'self-end rounded-md bg-ink px-2.5 py-1.5 text-white' : 'self-start rounded-md bg-panel px-2.5 py-1.5'}>
                    <pre className="font-sans text-[13px] break-words whitespace-pre-wrap">{m.content}</pre>
                  </div>
                ))}
              </div>
            )}
            <Field label={history.length ? 'Next message' : 'User message'} hint={version && 'userTemplate' in version.config && version.config.userTemplate ? 'optional — the template is sent too' : undefined}>
              <Textarea rows={3} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => (e.metaKey || e.ctrlKey) && e.key === 'Enter' && run.mutate()} />
            </Field>
            {version && 'tools' in version.config && (version.config.tools?.length ?? 0) > 0 && (
              <Field label="Mock tool results" hint={'"text" or {"tool": "result"}'}>
                <Textarea rows={2} className="mono" value={mocks} onChange={(e) => setMocks(e.target.value)} placeholder='{"search_jobs": "[]"}' />
              </Field>
            )}
            <div className="flex gap-2">
              <Button variant="primary" loading={run.isPending} onClick={() => run.mutate()}>
                <Play className="size-3.5" /> Run
              </Button>
              {history.length > 0 && (
                <Button variant="ghost" onClick={() => (setHistory([]), setLast(null))}>
                  <RotateCcw className="size-3.5" /> New conversation
                </Button>
              )}
            </div>
            <p className="text-xs text-ink-3">
              Runs call the real model and are logged as playground requests. Code tools are replaced with mocks.{streamUrl ? ' Output streams as it is generated.' : ''}
            </p>
            {run.error ? <ErrorNote error={run.error} /> : null}
          </div>
        </Card>
      </div>

      <div className="flex min-w-0 flex-col gap-5">
        {run.isPending &&
          (live ? (
            <Card title={<span className="flex items-center gap-2">Streaming <Badge tone="brand">live</Badge></span>}>
              <div className="flex flex-col gap-2 p-4">
                {live.events.map((e, i) => (
                  <div key={i} className="mono text-xs text-ink-2">
                    {e}
                  </div>
                ))}
                <pre className="min-h-8 rounded-md border border-line p-3 break-words whitespace-pre-wrap">{live.text || '…'}</pre>
              </div>
            </Card>
          ) : (
            <Loading label="Running…" />
          ))}
        {last && (
          <Card
            title={
              <span className="flex items-center gap-2">
                Result {last.ok ? <Badge tone="ok">ok</Badge> : <Badge tone="bad">error</Badge>}
                {last.activeAgent && last.activeAgent !== detail.agent.slug && <Badge tone="brand">answered by {last.activeAgent}</Badge>}
              </span>
            }
            actions={
              last.traceId && (
                <Link to="/requests/$traceId" params={{ traceId: last.traceId }} className="flex items-center gap-1 text-xs text-ink-2 hover:text-ink">
                  Open trace <ExternalLink className="size-3" />
                </Link>
              )
            }
          >
            <div className="p-4">
              {last.ok ? <JsonView value={last.output} /> : <ErrorNote error={new Error(last.error)} />}
              {last.usage && (
                <p className="mt-2 text-xs text-ink-3">
                  {fmtInt(last.usage.inputTokens)} in / {fmtInt(last.usage.outputTokens)} out · {fmtUsd(last.usage.costUsd)} · {fmtMs(trace.data?.trace.durationMs)}
                </p>
              )}
            </div>
          </Card>
        )}
        {trace.data && <TraceView detail={trace.data} />}
      </div>
    </div>
  );
};
